import React, { useEffect, useState, useMemo } from "react";
import {
  Container,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
  Dropdown,
  Nav,
  NavItem,
  NavLink,
  Row,
  Card,
  Col,
  Badge,
  Button,
  Input,
} from "reactstrap";
import classnames from "classnames";

import DeleteModal from "../../../Components/Common/DeleteModal";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import { Rating, Published, Price } from "./EcommerceProductCol";

//redux
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

// RTK Query hooks
import {
  useGetProductsQuery,
  // useDeleteProductMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { IsValidPermissions } from "Routes/AuthProtected";

import ImportExcelModal from "../../../Components/Common/ImportExcelModal";
import ImportSupplierCostExcelModal from "../../../Components/Common/ImportSupplierCostExcelModal";

// Define types for our API responses
interface Product {
  id: number;
  name: string;
  description: string;
  brand_company: string;
  model_number: string;
  size: string;
  weight: string;
  dimensions: string;
  status: number;
  category: number;
  category_name: string;
  supplier: number | null;
  measurement: number;
  measurement_name: string;
  tags: number[];
  tag_names: string[];
  thumbnail: string | null;
  thumbnail_url: string | null;
  attachment: string | null;
  attachment_url: string | null;
  attributes: Record<string, any>;
  created_at: string;
  modified_at: string;
}

interface ProductsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Product[];
}

const Inventory = () => {
  const dispatch = useDispatch();
  const history = useNavigate();

  // State for page and filters
  const [pageState, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
    status: string;
  }>({
    page: 1,
    pageSize: 10,
    search: "",
    status: "all",
  });

  const [productList, setProductList] = useState<Product[]>([]);
  const [activeTab, setActiveTab] = useState<string>("1");
  const [product, setProduct] = useState<Product | null>(null);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState<boolean>(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [isImportExcel, setIsImportExcel] = useState<boolean>(false);
  const [isExportExcel, setIsExportExcel] = useState<boolean>(false);
  const [isSupplierImportExcel, setIsSupplierImportExcel] =
    useState<boolean>(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen(!dropdownOpen);

  // RTK Query hooks - using pageState as the argument
  const {
    data: productsResponse,
    isLoading: productsLoading,
    isFetching: productFetching,
    error: productsError,
    refetch: refetchProducts,
  } = useGetProductsQuery(pageState);

  // Update product list when data is fetched
  useEffect(() => {
    if (productsResponse?.results) {
      setProductList(productsResponse.results);
    }
  }, [productsResponse]);

  // Handle errors
  useEffect(() => {
    if (productsError) {
      toast.error("Failed to load products");
    }
  }, [productsError]);

  // Handle tab change - update pageState which will trigger API call
  const toggleTab = (tab: string, status: number | "all") => {
    if (activeTab !== tab) {
      setActiveTab(tab);

      // Convert status to string format for API
      const statusValue = status === "all" ? "all" : status.toString();
      // Update pageState with new status and reset to page 1
      setPageState((prev) => ({
        ...prev,
        status: statusValue,
        page: 1, // Reset to first page when changing tabs
        search: "", // Clear search when changing tabs
      }));
    }
  };

  // Handle search - update pageState which will trigger API call
  const handleSearch = (searchTerm: string) => {
    setPageState((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1, // Reset to first page when searching
      status: "all",
    }));

    toggleTab("1", "all");
  };

  // Debounced search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      // The search is now handled directly through pageState updates
      // The API will automatically refetch when pageState changes
    }, 500);

    return () => clearTimeout(timer);
  }, [pageState.search]);

  const openProductView = (productId: number) => {
    history(`/add-inventory-product/${productId}?mode=view`);
  };

  //delete product
  const onClickDelete = (product: Product) => {
    setProduct(product);
    setDeleteModal(true);
  };

  // Handle product selection
  const handleProductSelection = (productId: number, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProducts((prev) => [...prev, productId]);
    } else {
      setSelectedProducts((prev) => prev.filter((id) => id !== productId));
    }
  };

  // Select all products
  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked) {
      setSelectedProducts(productList.map((product) => product.id));
    } else {
      setSelectedProducts([]);
    }
  };

  // Get status badge color
  const getStatusBadge = (status: number) => {
    switch (status) {
      case 1:
        return "secondary"; // Draft
      case 2:
        return "success"; // Published
      case 3:
        return "danger"; // Disabled
      default:
        return "secondary";
    }
  };

  // Get status text
  const getStatusText = (status: number) => {
    switch (status) {
      case 1:
        return "Draft";
      case 2:
        return "Published";
      case 3:
        return "Disabled";
      default:
        return "Unknown";
    }
  };

  const columns = useMemo(
    () => [
      // {
      //   Header: "#",
      //   Cell: (cell: any) => {
      //     return (
      //       <input
      //         type="checkbox"
      //         className="productCheckBox form-check-input"
      //         checked={selectedProducts.includes(cell.row.original.id)}
      //         onChange={(e) =>
      //           handleProductSelection(cell.row.original.id, e.target.checked)
      //         }
      //       />
      //     );
      //   },
      // },
      {
        Header: "Product",
        Cell: (product: any) => (
          <>
            <div className="d-flex align-items-center">
              <div className="flex-shrink-0 me-3">
                <div className="avatar-sm bg-light rounded p-1">
                  <img
                    src={
                      product.row.original.attachment_urls[0]?.file_url ||
                      "/path/to/placeholder.png"
                    }
                    alt=""
                    className="img-fluid d-block"
                    style={{ maxHeight: "40px", objectFit: "cover" }}
                  />
                </div>
              </div>
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1">
                  <Link
                    to={`#`}
                    className="text-body"
                    onClick={(event) => {
                      event.preventDefault();
                      openProductView(product.row.original.id);
                    }}
                  >
                    {" "}
                    {product.row.original.name}
                  </Link>
                </h5>
                <p className="text-muted mb-0">
                  Category:{" "}
                  <span className="fw-medium">
                    {" "}
                    {product.row.original.category_name}
                  </span>
                </p>
                <p className="text-muted mb-0">
                  Model Number:{" "}
                  <span className="fw-medium">
                    {" "}
                    {product.row.original.model_number}
                  </span>
                </p>
              </div>
            </div>
          </>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        filterable: false,
        Cell: (cellProps: any) => {
          return (
            <Badge
              color={getStatusBadge(cellProps.row.original.status)}
              className={`badge bg-${getStatusBadge(
                cellProps.row.original.status
              )}-subtle text-${getStatusBadge(cellProps.row.original.status)}`}
            >
              {getStatusText(cellProps.row.original.status)}
            </Badge>
          );
        },
      },
      {
        Header: "Measurement",
        accessor: "measurement_name",
        filterable: false,
      },
      {
        Header: "Tags",
        accessor: "tag_names",
        filterable: false,
        Cell: (cellProps: any) => {
          return (
            <div>
              {cellProps.row.original.tag_names &&
              cellProps.row.original.tag_names.length > 0 ? (
                cellProps.row.original.tag_names.map(
                  (tag: string, index: number) => (
                    <Badge key={index} color="light" className="me-1 text-dark">
                      {tag}
                    </Badge>
                  )
                )
              ) : (
                <span className="text-muted">No tags</span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Created",
        accessor: "created_at",
        filterable: false,
        Cell: (cellProps: any) => {
          return new Date(
            cellProps.row.original.created_at
          ).toLocaleDateString();
        },
      },
      {
        Header: "Created By",
        accessor: "created_by_name",
        filterable: false,
        Cell: (cellProps: any) =>
          cellProps.row.original.created_by_name || "Not recorded",
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                {(IsValidPermissions(["VIEW_ONLY_INVENTORY_PRODUCTS"]) ||
                  IsValidPermissions(["VIEW_ALL_INVENTORY_PRODUCTS"])) && (
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      openProductView(cellProps.row.original.id);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                )}
                {IsValidPermissions(["EDIT_INVENTORY_PRODUCTS"]) && (
                  <DropdownItem
                    tag={Link}
                    to={`/add-inventory-product/${cellProps.row.original.id}`}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [history, selectedProducts]
  );

  document.title = "Inventory | Puretech";

  if (productsLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Inventory" pageTitle="Resource Inventory" />
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />

      <ImportExcelModal
        show={isImportExcel}
        onCloseClick={() => setIsImportExcel(false)}
        import_type={"resource_inventory_import"}
        refetch={refetchProducts}
      />

      <ImportExcelModal
        show={isExportExcel}
        onCloseClick={() => setIsExportExcel(false)}
        import_type={"purchase_inventory_export"}
        refetch={refetchProducts}
      />

      <ImportSupplierCostExcelModal
        show={isSupplierImportExcel}
        onCloseClick={() => setIsSupplierImportExcel(false)}
        import_type={"supplier_cost_import"}
        refetch={refetchProducts}
      />

      {/* <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProduct}
        onCloseClick={() => setDeleteModal(false)}
        // deleting={deleting}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={deleteMultiple}
        onCloseClick={() => setDeleteModalMulti(false)}
        // deleting={deleting}
      /> */}
      <Container fluid>
        <BreadCrumb title="Inventory Catalog" pageTitle="Resource Inventory" />

        <Row>
          <Col xl={12} lg={12}>
            <div>
              <Card>
                <div className="card-header border-0">
                  <Row className="align-items-center">
                    <Col>
                      <Nav
                        className="nav-tabs-custom card-header-tabs border-bottom-0"
                        role="tablist"
                      >
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "1" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("1", "all");
                            }}
                            href="#"
                          >
                            All{" "}
                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {productsResponse?.published_count +
                                productsResponse?.draft_count +
                                productsResponse?.disabled_count}
                            </span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "2" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("2", 2);
                            }}
                            href="#"
                          >
                            Published{" "}
                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {productsResponse?.published_count}
                            </span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "3" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("3", 1);
                            }}
                            href="#"
                          >
                            Draft{" "}
                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {productsResponse?.draft_count}
                            </span>
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames(
                              { active: activeTab === "4" },
                              "fw-semibold"
                            )}
                            onClick={() => {
                              toggleTab("4", 3);
                            }}
                            href="#"
                          >
                            Disabled{" "}
                            <span className="badge bg-danger-subtle text-danger align-middle rounded-pill ms-1">
                              {productsResponse?.disabled_count}
                            </span>
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </Col>
                    <Col sm="auto" className="ms-auto">
                      <div className="d-flex align-items-center gap-2">
                        {/* Search Input */}
                        <div className="search-box">
                          <Input
                            type="text"
                            className="form-control"
                            placeholder="Search products..."
                            value={pageState.search}
                            onChange={(e) => handleSearch(e.target.value)}
                          />
                          <i className="ri-search-line search-icon"></i>
                        </div>

                        {/* Add Inventory Button */}
                        {IsValidPermissions(["ADD_INVENTORY_PRODUCTS"]) && (
                          <Link
                            to="/add-inventory-product"
                            className="btn btn-success"
                          >
                            <i className="ri-add-line align-bottom me-1"></i>{" "}
                            Add Inventory
                          </Link>
                        )}
                        {IsValidPermissions(["ADD_INVENTORY_PRODUCTS"]) && (
                          <a
                            type="button"
                            className="btn btn-secondary"
                            rel="noopener noreferrer"
                            target="_blank"
                            href={`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/products/?export=[name,model_number,item_group_code,item_type,measurement__name]&type=csv&filename=inventory_export`}
                          >
                            <i className="ri-file-download-line align-bottom me-1"></i>
                            Export Products
                          </a>
                        )}
                        {IsValidPermissions(["ADD_INVENTORY_PRODUCTS"]) && (
                          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                            <DropdownToggle caret color="secondary">
                              Bulk Import
                            </DropdownToggle>

                            <DropdownMenu>
                              <DropdownItem header>Actions</DropdownItem>

                              <DropdownItem>
                                <button
                                  type="button"
                                  className="btn btn-secondary w-100"
                                  onClick={() => setIsImportExcel(true)}
                                >
                                  <i className="ri-file-download-line align-bottom me-1"></i>
                                  Import Products
                                </button>
                              </DropdownItem>
                              <DropdownItem>
                                <button
                                  type="button"
                                  className="btn btn-secondary w-100"
                                  onClick={() => setIsSupplierImportExcel(true)}
                                >
                                  <i className="ri-file-download-line align-bottom me-1"></i>
                                  Import Supplier Cost
                                </button>
                              </DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        )}
                      </div>
                    </Col>
                    {selectedProducts.length > 0 && (
                      <div className="col-auto">
                        <div id="selection-element">
                          <div className="my-n1 d-flex align-items-center text-muted">
                            Select{" "}
                            <div
                              id="select-content"
                              className="text-body fw-semibold px-1"
                            >
                              {selectedProducts.length}
                            </div>{" "}
                            Result{" "}
                            <button
                              type="button"
                              className="btn btn-link link-danger p-0 ms-3"
                              onClick={() => setDeleteModalMulti(true)}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Row>
                </div>
                <div className="card-body pt-0">
                  {productList && productList.length > 0 ? (
                    <TableContainer
                      columns={columns}
                      data={productList || []}
                      isGlobalFilter={false}
                      isAddUserList={false}
                      customPageSize={10}
                      divClass="table-responsive mb-1"
                      tableClass="mb-0 align-middle table-borderless"
                      theadClass="table-light text-muted"
                      isProductsFilter={false}
                      SearchPlaceholder="Search Products..."
                      isPagination={false}
                      isAddProducts={false}
                      isServerPagination={true}
                      serverPaginationProps={{
                        count: productsResponse?.count || 0,
                        next: productsResponse?.next || null,
                        page_size: pageState.pageSize,
                        previous: productsResponse?.previous || null,
                        current_page: pageState.page,
                        total_page: Math.ceil(
                          (productsResponse?.count || 0) / pageState.pageSize
                        ),
                      }}
                      setPageState={setPageState}
                      isLoading={productsLoading || productFetching}
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <div>
                        <i className="ri-search-line display-5 text-success"></i>
                      </div>

                      <div className="mt-4">
                        <h5>Sorry! No Result Found</h5>
                        <p className="text-muted">
                          {pageState.search
                            ? `No products found for "${pageState.search}"`
                            : "No products available"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Inventory;
